/**
 * The two checkbox panels on the generate page.
 *
 * Both are pure, controlled and mode-aware; both are here rather than in `components/generate`
 * because neither is reusable — they exist to collect exactly the fields `GeneratePage` sends
 * and `GenerationDoc` records, and a shape that specific is not a component library entry.
 */

import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";

import { PUBLICATION_CONTEXT_LABELS, type PublicationContext } from "@/constants/generate";
import { RADIUS_INNER_PX, jp } from "@/theme/tokens";

/**
 * Where the image is intended to be published — multiple choice, since the same image often
 * serves more than one context at once (an annual report photo that also runs on the corporate
 * website).
 *
 * **Checkboxes rather than a `<select multiple>`**: a native multi-select needs a modifier key
 * to pick more than one option, which is undiscoverable and worse with a keyboard or a screen
 * reader.
 *
 * "Not specified" is mutually exclusive with every real option — checking it clears the rest,
 * and checking a real one clears it — so the value can never become the nonsensical
 * `["unspecified", "advertising"]`. The backend re-derives the same rule for any caller that
 * bypasses this UI.
 */
export function PublicationContextSelector({
  value,
  onChange,
  disabled = false,
}: {
  value: PublicationContext[];
  onChange: (value: PublicationContext[]) => void;
  disabled?: boolean;
}) {
  const isUnspecified = value.length === 0 || value.includes("unspecified");

  function toggle(option: PublicationContext) {
    if (option === "unspecified") {
      onChange(["unspecified"]);
      return;
    }
    const withoutUnspecified = value.filter((entry) => entry !== "unspecified");
    const next = withoutUnspecified.includes(option)
      ? withoutUnspecified.filter((entry) => entry !== option)
      : [...withoutUnspecified, option];
    // Never empty: clearing the last real context means "not specified", not "nothing".
    onChange(next.length > 0 ? next : ["unspecified"]);
  }

  return (
    <Box
      component="fieldset"
      aria-label="Intended publication context"
      disabled={disabled}
      className="grid gap-1.5 sm:grid-cols-2"
      sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
    >
      {(Object.keys(PUBLICATION_CONTEXT_LABELS) as PublicationContext[]).map((option) => (
        <FormControlLabel
          key={option}
          sx={{
            m: 0,
            px: 1.5,
            py: 0.5,
            borderRadius: `${RADIUS_INNER_PX}px`,
            border: `1px solid ${jp.line}`,
            backgroundColor: "rgba(255,255,255,0.6)",
          }}
          control={
            <Checkbox
              size="small"
              checked={option === "unspecified" ? isUnspecified : !isUnspecified && value.includes(option)}
              disabled={disabled}
              onChange={() => toggle(option)}
            />
          }
          label={<Typography variant="body2">{PUBLICATION_CONTEXT_LABELS[option]}</Typography>}
        />
      ))}
    </Box>
  );
}

/**
 * Rights and consent capture.
 *
 * **Two distinct shapes per mode, hence a discriminated union rather than a pile of optional
 * props.** Edit mode is about a real uploaded photograph — rights of use, then subject consent
 * if it shows people. Describe mode has no photograph to have rights to, only a disclosure
 * that any people in the result are entirely synthetic.
 *
 * The consent question appears only once "this shows identifiable people" is ticked: asking it
 * unconditionally would train people to tick it without reading.
 */
export type RightsConsentPanelProps =
  | {
      mode: "edit";
      depictsIdentifiablePeople: boolean;
      onDepictsIdentifiablePeopleChange: (value: boolean) => void;
      sourceRightsConfirmed: boolean;
      onSourceRightsConfirmedChange: (value: boolean) => void;
      subjectConsentConfirmed: boolean;
      onSubjectConsentConfirmedChange: (value: boolean) => void;
      disabled?: boolean;
    }
  | {
      mode: "describe";
      depictsIdentifiablePeople: boolean;
      onDepictsIdentifiablePeopleChange: (value: boolean) => void;
      syntheticPeopleDisclosureAcknowledged: boolean;
      onSyntheticPeopleDisclosureAcknowledgedChange: (value: boolean) => void;
      disabled?: boolean;
    };

export function RightsConsentPanel(props: RightsConsentPanelProps) {
  const { mode, depictsIdentifiablePeople, onDepictsIdentifiablePeopleChange } = props;
  const disabled = props.disabled ?? false;

  return (
    <Stack
      spacing={1.5}
      sx={{
        p: 2,
        border: `1px solid ${jp.line}`,
        backgroundColor: "rgba(255,255,255,0.8)",
      }}
    >
      <Typography variant="overline" color="primary.dark">
        Rights &amp; consent
      </Typography>

      {props.mode === "edit" ? (
        <CheckboxRow
          checked={props.sourceRightsConfirmed}
          disabled={disabled}
          onChange={props.onSourceRightsConfirmedChange}
          label="I have the right to use and edit this photograph — I took it, or have permission from the rights holder."
        />
      ) : null}

      <CheckboxRow
        checked={depictsIdentifiablePeople}
        disabled={disabled}
        onChange={onDepictsIdentifiablePeopleChange}
        label={
          mode === "edit"
            ? "This photograph shows identifiable people (employees, customers, models, or anyone recognisable)."
            : "This brief describes identifiable people (specific named individuals, not generic or illustrative figures)."
        }
      />

      {depictsIdentifiablePeople ? (
        <Box
          sx={{
            p: 1.5,
            border: "1px solid rgba(217,119,6,0.4)",
            backgroundColor: "rgba(217,119,6,0.05)",
          }}
        >
          {props.mode === "edit" ? (
            <CheckboxRow
              checked={props.subjectConsentConfirmed}
              disabled={disabled}
              onChange={props.onSubjectConsentConfirmedChange}
              label="Everyone identifiable in this photograph has consented to it being used and edited with AI."
            />
          ) : (
            <CheckboxRow
              checked={props.syntheticPeopleDisclosureAcknowledged}
              disabled={disabled}
              onChange={props.onSyntheticPeopleDisclosureAcknowledgedChange}
              label="I understand any people in the result will be entirely AI-generated — not a real photograph of the individuals described — and will disclose this wherever that distinction matters."
            />
          )}
        </Box>
      ) : null}
    </Stack>
  );
}

/** `alignItems: flex-start` so a two-line label does not centre its checkbox halfway down. */
function CheckboxRow({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <FormControlLabel
      sx={{ alignItems: "flex-start", m: 0 }}
      control={
        <Checkbox
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          sx={{ mt: -0.5 }}
        />
      }
      label={
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      }
    />
  );
}
